import { useApp } from '@/app/AppContext'
import { formatCell } from '@/lists/DataTable'
import type { CardConflict } from './types'

/**
 * Разрешение конфликта параллельной правки (тикет #66, ТЗ §5.1).
 *
 * ## Почему обе стороны на экране
 *
 * «Кто-то изменил запись, попробуйте ещё раз» — это отказ, а не разрешение конфликта:
 * пользователь перезагружает карточку, теряет свою правку и вводит её заново, не зная,
 * что именно поменял сосед. Обе версии рядом превращают вопрос «повторить?» в вопрос
 * «чьё значение верно?», на который у человека есть ответ.
 *
 * ## Почему нет кнопки «слить автоматически»
 *
 * Автоматического победителя у расхождения нет — этому в проекте уже посвящён обмен с 1С
 * (#54): «последний выигрывает» неверен ровно в том случае, ради которого пишется, а «наша
 * сторона главная» верна примерно для половины полей и неверна невидимо. Решает человек,
 * поле за полем.
 */

interface ConflictPanelProps {
  conflict: CardConflict
  /** Взять серверное значение поля. */
  onTakeTheirs: (field: string) => void
  /** Настоять на своём: значение уйдёт поверх серверного. */
  onKeepMine: (field: string) => void
  /** Отказаться от правки целиком и перечитать запись. */
  onDiscard: () => void
}

export function ConflictPanel({
  conflict,
  onTakeTheirs,
  onKeepMine,
  onDiscard,
}: ConflictPanelProps) {
  const { t, locale } = useApp()

  return (
    <section className="card-conflict" role="alert" aria-labelledby="conflict-title">
      <h2 id="conflict-title">{t('card.conflict.title')}</h2>
      <p>{t('card.conflict.explain')}</p>

      <table className="conflict-table">
        <thead>
          <tr>
            <th>{t('card.conflict.field')}</th>
            <th>{t('card.conflict.theirs')}</th>
            <th>{t('card.conflict.mine')}</th>
            <th>{t('card.conflict.decision')}</th>
          </tr>
        </thead>
        <tbody>
          {conflict.fields.map((field) => (
            <tr key={field}>
              <th scope="row">{t(`column.${field}`)}</th>
              <td>{formatCell(conflict.theirs.values[field], locale) || '—'}</td>
              <td>{formatCell(conflict.mine[field], locale) || '—'}</td>
              <td className="conflict-actions">
                <button type="button" onClick={() => onTakeTheirs(field)}>
                  {t('card.conflict.takeTheirs')}
                </button>
                <button type="button" onClick={() => onKeepMine(field)}>
                  {t('card.conflict.keepMine')}
                </button>
              </td>
            </tr>
          ))}

          {conflict.fields.length === 0 && (
            <tr>
              {/* Расхождение по версии без расхождения по полям: сосед поменял то,
                  чего на этом экране нет. Показать пустую таблицу молча значило бы
                  оставить пользователя гадать, что именно ему решать. */}
              <td colSpan={4} className="muted">
                {t('card.conflict.otherFields')}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <button type="button" onClick={onDiscard}>
        {t('card.conflict.discard')}
      </button>
    </section>
  )
}
